/**
 * The item opener a screen's item buttons use (itemEditorLink's ItemEditorActions, the override
 * dialog's field menus, Open Item on the AIM list...): the Quick item dialog and the whole item page,
 * over the screen. Every page has one (SiteItemEditorHost in MainLayout); a screen can nest its own
 * (useQuickItemHost). Nothing opens a new browser tab (Jeff 2026-11-03).
 */
import { createContext, useContext } from 'react';

/** A new item made for a picker: its key, name, and its size when exactly one was added. */
export interface PickedItem {
  itemId: number;
  itemName: string;
  sizeId: number | null;
}

export interface ItemEditorOpener {
  /**
   * Edit an item in the Quick item dialog; `section` is where "Open full item" opens the page. A role that
   * can only view items gets the whole item page, read only.
   */
  openItem: (itemId: number, section?: string) => void;
  /**
   * A new item in the dialog; `onPicked` gets it, with its size when it has exactly one, once saved.
   * `fromItemId` starts it as an unsaved copy of that item (New From Existing).
   */
  newItem: (onPicked: (item: PickedItem) => void, fromItemId?: number) => void;
  /** The whole item page over the screen, on `section`. */
  openFull?: (itemId: number, section?: string) => void;
}

export const ItemEditorOpenerContext = createContext<ItemEditorOpener | null>(null);

/** The screen's item opener, or null where there is none (no Items access, or outside the site's pages). */
export function useItemEditorOpener(): ItemEditorOpener | null {
  return useContext(ItemEditorOpenerContext);
}
